import React from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { Panel } from 'primereact/panel';
import { Button } from 'primereact/button';
import PropTypes from 'prop-types';
import {
  userCanUpdateCompanyData,
  userHasMemberRole,
} from '../../services/apps/side-menu-builders/SideMenuHelper';
import { getCreditPlanDisplayName } from '../../utils/AppUtil';

const messages = defineMessages({
  standard: {
    id: 'dashboard.dashboard-account-information.standard-credits-consumption',
    defaultMessage: 'Standard credits consumption',
  },
  unlimited: {
    id: 'dashboard.dashboard-account-information.unlimited-credits',
    defaultMessage: 'Unlimited',
  },
  noLimit: {
    id: 'dashboard.dashboard-account-information.no-limit',
    defaultMessage: 'NoLimit',
  },
  plan: {
    id: 'lbl.plan',
    defaultMessage: 'Plan',
  },
});

const getPermissionToEdit = (role, isMotherAccount) => {
  if (isMotherAccount) {
    return userCanUpdateCompanyData(role);
  }

  return !userHasMemberRole(role);
};

const CompanyDataDetail = ({ account, countryDisplayName, toggleEdit, role }) => {
  const { formatMessage } = useIntl();

  const { currentCreditPlan } = account;

  const pencilIcon = 'fa fa-pencil';
  const hasPermissionToEdit = getPermissionToEdit(role, account.isHeadOfFamily);

  const creditPlanDisplayName = getCreditPlanDisplayName(
    currentCreditPlan,
    formatMessage(messages.standard),
    formatMessage(messages.unlimited),
    formatMessage(messages.noLimit),
  );

  const { taxData } = account.companyInformation;
  const taxId = taxData ? taxData.taxId : '-';
  const taxIdType = taxData
    ? taxData.country && taxData.description
      ? `${taxData.country} - ${taxData.description}`
      : '-'
    : '-';

  return (
    <div>
      <Panel
        header={
          <div className="p-grid p-nogutter">
            <div className="p-col">
              <FormattedMessage
                id="settings.invoice-address-and-other-company-data"
                defaultMessage="Invoice Address and other Company Data"
              />
            </div>
            <div className="p-col-2 align-right">
              {hasPermissionToEdit && (
                <Button
                  icon={pencilIcon}
                  type="button"
                  className="p-button-secondary p-button-sm"
                  onClick={toggleEdit}
                />
              )}
            </div>
          </div>
        }
      >
        <div>
          <FormattedMessage id="settings.company-data.legal-name" defaultMessage="Legal Name: " tagName="strong" />
          <span>{account.companyInformation.name}</span>
        </div>
        <br />
        <div>
          <FormattedMessage id="lbl.plan" defaultMessage="Plan" tagName="strong" />
          <strong>: </strong>
          <span>{creditPlanDisplayName}</span>
        </div>
        <br />
        <div>
          <FormattedMessage id="attrs.organisation-size" defaultMessage="Organisation size: " tagName="strong" />
          <span>{account.companyInformation.totalNumberEmployees}</span>
        </div>
        <br />
        <div>
          <FormattedMessage
            id="attrs.credit-notification-threshold"
            defaultMessage="Credit Notification Threshold: "
            tagName="strong"
          />
          <span>{account.companyInformation.creditNotificationThreshold}</span>
        </div>
        <br />
        <div>
          <FormattedMessage id="settings.company-data.display-name" defaultMessage="Display Name: " tagName="strong" />
          <span>{account.companyInformation.displayName}</span>
        </div>
        <br />
        <div>
          <FormattedMessage
            id="settings.company-data.addressLine1"
            defaultMessage="Address Line 1: "
            tagName="strong"
          />
          <span>{account.companyInformation.addressLine1}</span>
        </div>
        <br />
        <div>
          <FormattedMessage
            id="settings.company-data.addressLine2"
            defaultMessage="Address Line 2: "
            tagName="strong"
          />
          <span>{account.companyInformation.addressLine2}</span>
        </div>
        <br />
        <div>
          <FormattedMessage id="settings.company-data.city" defaultMessage="City: " tagName="strong" />
          <span>{account.companyInformation.city}</span>
        </div>
        <br />
        <div>
          <FormattedMessage id="settings.company-data.state" defaultMessage="State: " tagName="strong" />
          <span>{account.companyInformation.state}</span>
        </div>
        <br />
        <div>
          <FormattedMessage id="settings.company-data.country" defaultMessage="Country: " tagName="strong" />
          <span>{countryDisplayName}</span>
        </div>
        <br />
        <div>
          <FormattedMessage id="settings.company-data.postalCode" defaultMessage="Postal Code: " tagName="strong" />
          <span>{account.companyInformation.postalCode}</span>
        </div>
        <br />
        <div>
          <FormattedMessage id="settings.company-data.vatCountry" defaultMessage="VAT/GST country" tagName="strong" />:{' '}
          <span>{taxIdType || '-'}</span>
        </div>
        <br />
        <div>
          <FormattedMessage id="settings.company-data.vatId" defaultMessage="VAT/GST ID" tagName="strong" />:{' '}
          <span>{taxId || '-'}</span>
        </div>
        <br />
        <div>
          <FormattedMessage
            id="settings.company-data.registration-number"
            defaultMessage="Registration Number: "
            tagName="strong"
          />
          <span>{account.companyInformation.registrationNumber}</span>
        </div>
        <br />
        <div>
          <FormattedMessage
            id="settings.company-data.dpo-email"
            defaultMessage="Data Protection Officer email: "
            tagName="strong"
          />
          <span>{account.companyInformation.dpoEmail}</span>
        </div>
      </Panel>
    </div>
  );
};

CompanyDataDetail.propTypes = {
  account: PropTypes.objectOf(PropTypes.any).isRequired,
  countryDisplayName: PropTypes.string.isRequired,
  role: PropTypes.string.isRequired,
  toggleEdit: PropTypes.func.isRequired,
};

export default CompanyDataDetail;
